import { PermitCategory } from "../../../common/enums";
import { String_extractIndex } from "../../../common/stringUtils";
import Position from "../../Position";
import Errors from "../../responses/Errors";

type PositionPickerRow = {
    id: number;
    isActive: boolean;
    longitude: number | undefined;
    longitudeErrorMessage?: string;
    latitude: number | undefined;
    latitudeErrorMessage?: string;
    comment: string | undefined;
    commentErrorMessage?: string;
    order: number;
    polygon: any;
    category: PermitCategory | undefined;
};

export default PositionPickerRow;

export function PositionPickerRow_toPosition(positionPickerRow: PositionPickerRow): Position {
    const position: Position = {
        id: positionPickerRow.id,
        comment: positionPickerRow.comment,
        isActive: positionPickerRow.isActive,
        latitude: positionPickerRow.latitude ?? 0,
        longitude: positionPickerRow.longitude ?? 0,
        order: positionPickerRow.order,
        polygonJsonObject: positionPickerRow.polygon ? JSON.stringify(positionPickerRow.polygon) : null,
        polygon: positionPickerRow.polygon,
        category: positionPickerRow.category
    };

    return position;
}

export function PositionPickerRow_default(): PositionPickerRow {
    const position: PositionPickerRow = {
        id: 0,
        comment: "",
        isActive: false,
        latitude: undefined,
        longitude: undefined,
        order: 0,
        polygon: undefined,
        category: undefined
    };

    return position;
}

/**
 * Returns a new array of PositionPickerRow with error messages populated from the given errors object.
 * @param formElements The array of PositionPickerRow to update. Will not be mutated.
 * @param errors The errors object to use for populating the error messages. Each error key needs to have format "[index].Property". For example, "[0].Latitude" or "[32].Comment".
 */
export function PositionPickerRows_populateErrorMessages(
    positions: PositionPickerRow[],
    errors: Errors
): PositionPickerRow[] {
    const errorKeys = Object.keys(errors);

    const groupedPositionErrors: { [positionIndex: number]: Errors } = {};

    errorKeys.forEach((k) => {
        const index = String_extractIndex(k);

        if (!Number.isInteger(index)) {
            console.error("Invalid index extracted from error key", index);
            return;
        }

        if (!groupedPositionErrors[index as number]) {
            groupedPositionErrors[index as number] = {};
        }

        const reformatedKey = k.substring(k.indexOf(".") + 1);
        groupedPositionErrors[index as number][reformatedKey] = errors[k];
    });

    const updatedPositionPickerRows: PositionPickerRow[] = positions.map((p, index) => {
        const positionErrors = groupedPositionErrors[index];

        const updatedPosition: PositionPickerRow = {
            ...positions[index],
            latitudeErrorMessage: undefined,
            longitudeErrorMessage: undefined,
            commentErrorMessage: undefined
        };

        if (!positionErrors) {
            return updatedPosition;
        }

        Object.keys(positionErrors).forEach((k) => {
            const isLatitudeError = k.endsWith("Latitude");
            const isLongitudeError = k.endsWith("Longitude");
            const isCommentError = k.endsWith("Comment");

            if (isLatitudeError) {
                updatedPosition.latitudeErrorMessage = positionErrors[k][0];
            } else if (isLongitudeError) {
                updatedPosition.longitudeErrorMessage = positionErrors[k][0];
            } else if (isCommentError) {
                updatedPosition.commentErrorMessage = positionErrors[k][0];
            }
        });

        return updatedPosition;
    });

    return updatedPositionPickerRows;
}

export function PositionPickerRow_clearErrors(positionPickerRow: PositionPickerRow): PositionPickerRow {
    const updatedPositionPickerRow: PositionPickerRow = {
        ...positionPickerRow,
        latitudeErrorMessage: undefined,
        longitudeErrorMessage: undefined,
        commentErrorMessage: undefined
    };

    return updatedPositionPickerRow;
}
